/**
 * Pure punto banco: card values, the mod-10 total, the fixed drawing tableau,
 * and settlement. No player decisions exist in this game — given four cards
 * the rest of the coup is determined — so the whole engine is a set of
 * functions over card arrays.
 *
 * No room, no sockets, no shoe: the server layer supplies the cards and owns
 * the sequencing by calling `nextCoupDraw` until it returns null.
 */
import type { BaccaratRules } from './baccaratRules.js';
import type { Card } from './types.js';

/** The three spots a seat may back. Exactly one per seat per coup. */
export type BaccaratSpot = 'player' | 'banker' | 'tie';

export const BACCARAT_SPOTS: readonly BaccaratSpot[] = ['player', 'banker', 'tie'];

/** Which hand took the coup. A tie pushes the player and banker spots. */
export type BaccaratOutcome = 'player' | 'banker' | 'tie';

/** Which hand draws next, or null once the tableau is finished. */
export type BaccaratDraw = 'player' | 'banker' | null;

/** Ace is 1, pips are face value, and every ten-count card is worth nothing. */
export function baccaratCardValue(card: Card): number {
  switch (card.rank) {
    case 'A':
      return 1;
    case '10':
    case 'J':
    case 'Q':
    case 'K':
      return 0;
    default:
      return Number(card.rank);
  }
}

/** The hand total: pip sum mod 10, so 9 + K is 9 and 5 + 5 + 5 is 5. */
export function baccaratTotal(cards: readonly Card[]): number {
  return cards.reduce((sum, card) => sum + baccaratCardValue(card), 0) % 10;
}

/** A natural is 8 or 9 on the *first two* cards; a drawn 9 does not count. */
export function isNatural(cards: readonly Card[]): boolean {
  return cards.length === 2 && baccaratTotal(cards) >= 8;
}

/** The player line: draw on 0–5, stand on 6–7. */
export function playerDraws(total: number): boolean {
  return total <= 5;
}

/**
 * The banker line. With the player standing pat the banker plays the same
 * 0–5 line; once the player has drawn, the banker's decision turns on the
 * value of that third card.
 */
export function bankerDraws(total: number, playerThird?: number): boolean {
  if (playerThird === undefined) return total <= 5;
  switch (total) {
    case 0:
    case 1:
    case 2:
      return true;
    case 3:
      return playerThird !== 8;
    case 4:
      return playerThird >= 2 && playerThird <= 7;
    case 5:
      return playerThird >= 4 && playerThird <= 7;
    case 6:
      return playerThird >= 6 && playerThird <= 7;
    default:
      // 7 stands, and 8–9 never reaches the tableau — it is a natural.
      return false;
  }
}

/**
 * One step of the tableau. The caller deals the returned card onto that hand
 * and calls again; null means the coup is complete and ready to settle.
 *
 * Returns null for an incomplete deal too, so a half-dealt coup cannot draw.
 */
export function nextCoupDraw(player: readonly Card[], banker: readonly Card[]): BaccaratDraw {
  if (player.length < 2 || banker.length < 2) return null;
  // Either natural ends the coup on the spot, before anyone can draw.
  if (isNatural(player) || isNatural(banker)) return null;

  if (player.length === 2 && playerDraws(baccaratTotal(player))) return 'player';

  if (banker.length === 2) {
    const third = player.length > 2 ? baccaratCardValue(player[2]!) : undefined;
    if (bankerDraws(baccaratTotal(banker), third)) return 'banker';
  }

  return null;
}

/** Who won, by the higher mod-10 total. */
export function baccaratOutcome(player: readonly Card[], banker: readonly Card[]): BaccaratOutcome {
  const p = baccaratTotal(player);
  const b = baccaratTotal(banker);
  if (p > b) return 'player';
  if (b > p) return 'banker';
  return 'tie';
}

/**
 * Chips returned to a bettor, stake included. A loss pays 0, a push pays the
 * stake straight back.
 *
 * Banker winnings are floored before the stake is added, so a 5-chip bet at
 * the standard 5% commission wins 4, not 4.75 — chips are integers.
 */
export function settleBaccaratBet(
  spot: BaccaratSpot,
  stake: number,
  outcome: BaccaratOutcome,
  rules: BaccaratRules,
): number {
  if (outcome === 'tie') {
    if (spot === 'tie') return stake + Math.floor(stake * rules.tiePayout);
    // The player and banker spots are barred, not beaten: the stake comes back.
    return stake;
  }
  if (spot !== outcome) return 0;
  if (spot === 'player') return stake * 2;
  return stake + Math.floor(stake * (1 - rules.commission));
}

// --- Pair side bets --------------------------------------------------------

/**
 * Baccarat's side bets read one hand's opening two cards and nothing else,
 * which is a different shape from the blackjack `SideBetContext` (player cards
 * plus a dealer upcard). They get their own small registry rather than being
 * bent into that one.
 */
export type BaccaratSideBetId = 'player_pair' | 'banker_pair';

export const VALID_BACCARAT_SIDE_BETS: BaccaratSideBetId[] = ['player_pair', 'banker_pair'];

export interface BaccaratSideBetDef {
  id: BaccaratSideBetId;
  name: string;
  blurb: string;
  odds: string;
  /** The ":1" side of the odds; an 11:1 win has a multiplier of 11. */
  multiplier: number;
  /** Which hand's opening two cards the bet reads. */
  hand: 'player' | 'banker';
}

/** True when the opening two cards are the same rank. Suit is irrelevant. */
export function evaluatePair(cards: readonly Card[]): boolean {
  const [a, b] = cards;
  return !!a && !!b && a.rank === b.rank;
}

export const BACCARAT_SIDE_BETS: Record<BaccaratSideBetId, BaccaratSideBetDef> = {
  player_pair: {
    id: 'player_pair',
    name: 'Player Pair',
    blurb: "Pays when the player's first two cards are the same rank.",
    odds: '11:1',
    multiplier: 11,
    hand: 'player',
  },
  banker_pair: {
    id: 'banker_pair',
    name: 'Banker Pair',
    blurb: "Pays when the banker's first two cards are the same rank.",
    odds: '11:1',
    multiplier: 11,
    hand: 'banker',
  },
};

/** Chips returned by a pair side bet, stake included. 0 on a loss. */
export function settleBaccaratSideBet(
  id: BaccaratSideBetId,
  stake: number,
  player: readonly Card[],
  banker: readonly Card[],
): number {
  const def = BACCARAT_SIDE_BETS[id];
  const cards = def.hand === 'player' ? player : banker;
  // Only the opening two count: a pair made with a drawn third card is nothing.
  return evaluatePair(cards.slice(0, 2)) ? stake + stake * def.multiplier : 0;
}
